<script lang="ts">
  import { mdiDeleteOutline } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type {
    PublicContactMethodChannel,
    PublicContactMethodResponse,
  } from "../../../../../../../../api/public/appointment-portal"
  import {
    channelDisplayLabel,
    contactMethodChannelToggleOptions,
    contactMethodKindLabel,
    contactMethodStatusPresentation,
  } from "./_logic/presentation"

  const {
    method,
    pendingChannelKey = null,
    rowMessage = "",
    onSetChannelPermission,
    onRequestDeletion,
  } = $props<{
    method: PublicContactMethodResponse
    pendingChannelKey?: string | null
    rowMessage?: string
    onSetChannelPermission: (
      method: PublicContactMethodResponse,
      channel: PublicContactMethodChannel,
      isEnabled: boolean,
    ) => Promise<void> | void
    onRequestDeletion: (method: PublicContactMethodResponse) => void
  }>()

  const status = $derived(contactMethodStatusPresentation(method))
  const isReadOnly = $derived(Boolean(method.read_only || method.is_pending))
  const channelValue = (isEnabled: boolean) => (isEnabled ? "active" : "inactive")

  const selectChannelState = (channel: PublicContactMethodChannel, value: string) => {
    if (isReadOnly) return

    const nextEnabled = value === "active"
    const current = method.channels.find((entry) => entry.channel === channel)

    if (!current || current.is_enabled === nextEnabled) return

    onSetChannelPermission(method, channel, nextEnabled)
  }
</script>

<div class="contact-method-card">
  <ActionBox
    label={contactMethodKindLabel(method.kind)}
    tag={status.label}
    tagColor={status.color}
    darkMode={true}
  >
    <div class="contact-method-content">
      <div class="method-value">
        <span>Gespeicherter Wert</span>
        <strong>{method.masked_value}</strong>
      </div>

      <div class="channel-list">
        {#each method.channels as channel (channel.channel)}
          <div class="channel-row">
            <div class="channel-copy">
              <span>{channelDisplayLabel(method, channel.channel)}</span>
            </div>
            <ToggleCTA
              options={contactMethodChannelToggleOptions}
              value={method.deletion_requested ? "inactive" : channelValue(channel.is_enabled)}
              darkMode={false}
              small={true}
              disabled={Boolean(pendingChannelKey) || method.deletion_requested || isReadOnly}
              onChange={(value) => selectChannelState(channel.channel, value)}
              dataTestid={`contact-method.${method.id}.${channel.channel}`}
            />
          </div>
        {/each}
      </div>

      {#if rowMessage}
        <p class="row-message">{rowMessage}</p>
      {/if}
    </div>

    {#snippet buttonSnippet()}
      {#if !method.deletion_requested && !isReadOnly}
        <CTA
          type="tertiaryButton"
          icon={mdiDeleteOutline}
          content="Datenlöschung anfragen"
          onClick={() => onRequestDeletion(method)}
          darkMode={false}
          small={true}
        />
      {/if}
    {/snippet}
  </ActionBox>
</div>

<style lang="less">
  .contact-method-card {
    width: 100%;
  }

  .contact-method-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .method-value {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    span {
      color: var(--text-invers-100);
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;
    }

    strong {
      color: var(--text-invers-100);
      font-size: 1rem;
      overflow-wrap: anywhere;
    }
  }

  .channel-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .channel-row {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
  }

  .channel-copy {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;

    span {
      color: var(--text-invers-100);
      font-weight: 800;
    }
  }

  .row-message {
    color: var(--text-invers-100);
    line-height: 1.45;
    margin: 0;
  }

  @media (max-width: 640px) {
    .channel-row {
      align-items: flex-start;
      flex-direction: column;
    }
  }
</style>
